import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { FuelConsumptionTable } from "../components/FuelConsumptionTable";
import { Droplet, Car as CarIcon, ArrowRight, Gauge, User, CreditCard, List, Search, X, Pencil, Trash2 } from "lucide-react";
import AuditLine from "../components/AuditLine";
import ActivityFeed from "../components/ActivityFeed";
import { fmtRoDate } from "../lib/dateFmt";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Combustibil() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [recent, setRecent] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list | by_chip | by_person | by_car
  const [q, setQ] = useState("");
  const [drill, setDrill] = useState(null); // { type: 'chip'|'person'|'car', key, label }
  const [editing, setEditing] = useState(null); // fuel entry being edited (admin only)
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [form, setForm] = useState({
    car_id: "",
    data_alimentare: todayISO(),
    numar_chip: "",
    litri: "",
    numar_km: "",
    persoana: user?.name || user?.username || "",
  });

  const load = async () => {
    try {
      const [c, f, p] = await Promise.all([
        api.get("/cars"),
        api.get("/fuel/all?limit=1000").catch(() => ({ data: [] })),
        api.get("/persons", { params: { active: true } }).catch(() => ({ data: [] })),
      ]);
      setCars(c.data || []);
      const sorted = (f.data || []).slice().sort((a, b) => (b.data_alimentare || "").localeCompare(a.data_alimentare || ""));
      setRecent(sorted);
      const plist = Array.isArray(p.data) ? p.data : (p.data?.persons || []);
      setPersons(plist);
    } catch { toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const carLabel = (c) => {
    const intern = c.intern_number ? `Auto ${c.intern_number}` : null;
    const plate = c.numar_inmatriculare || null;
    const make = [c.marca, c.model].filter(Boolean).join(" ");
    return [intern, plate, make].filter(Boolean).join(" · ") || "—";
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.car_id) { toast.error("Selectează o mașină"); return; }
    if (!form.litri || Number(form.litri) <= 0) { toast.error("Introdu litrii"); return; }
    if (!form.numar_km || Number(form.numar_km) <= 0) { toast.error("Introdu km-ul"); return; }
    setLoading(true);
    try {
      await api.post(`/cars/${form.car_id}/fuel`, {
        data_alimentare: form.data_alimentare,
        numar_chip: form.numar_chip || null,
        litri: Number(form.litri),
        numar_km: Number(form.numar_km),
        persoana: form.persoana || null,
      });
      toast.success("Alimentare salvată");
      setForm({ ...form, litri: "", numar_km: "", numar_chip: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return recent;
    const s = q.trim().toLowerCase();
    return recent.filter((r) => [r.car_label, r.persoana, r.numar_chip, r.data_alimentare]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [recent, q]);

  const totalLiters = useMemo(() => filtered.reduce((a, r) => a + (Number(r.litri) || 0), 0), [filtered]);

  // Grupări pentru cele 3 view modes alternative
  const groupBy = (rows, keyFn) => {
    const m = new Map();
    for (const r of rows) {
      const k = keyFn(r);
      if (!k) continue;
      if (!m.has(k)) m.set(k, { key: k, items: [], total_liters: 0, total_fills: 0, last_date: "" });
      const g = m.get(k);
      g.items.push(r);
      g.total_liters += Number(r.litri) || 0;
      g.total_fills += 1;
      if ((r.data_alimentare || "") > g.last_date) g.last_date = r.data_alimentare || "";
    }
    return Array.from(m.values()).sort((a, b) => (b.last_date || "").localeCompare(a.last_date || ""));
  };

  const groupChip = useMemo(() => groupBy(filtered, (r) => r.numar_chip || null), [filtered]);
  const groupPerson = useMemo(() => groupBy(filtered, (r) => r.persoana || null), [filtered]);
  const groupCar = useMemo(() => groupBy(filtered, (r) => r.car_label || null), [filtered]);

  // Drill-down: filtru pentru lista dialog
  const drillItems = useMemo(() => {
    if (!drill) return [];
    return recent.filter((r) => {
      if (drill.type === "chip") return (r.numar_chip || null) === drill.key;
      if (drill.type === "person") return (r.persoana || null) === drill.key;
      if (drill.type === "car") return (r.car_label || null) === drill.key;
      return false;
    });
  }, [drill, recent]);

  // Pentru drill-down: arăți contextul invers (chip → ce mașini, persoană → ce mașini, mașină → ce chip-uri)
  const drillContext = useMemo(() => {
    if (!drill) return [];
    const ctxKey = drill.type === "car" ? "numar_chip" : "car_label";
    const ctxLabel = drill.type === "car" ? "chip-uri folosite" : "autoturisme alimentate";
    const set = new Map();
    for (const r of drillItems) {
      const k = r[ctxKey] || "—";
      if (!set.has(k)) set.set(k, { key: k, count: 0, liters: 0 });
      const x = set.get(k);
      x.count += 1;
      x.liters += Number(r.litri) || 0;
    }
    return { label: ctxLabel, rows: Array.from(set.values()).sort((a, b) => b.count - a.count) };
  }, [drill, drillItems]);

  return (
    <div className="space-y-5" data-testid="page-combustibil">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center text-[#D97706]">
            <Droplet />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Combustibil</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Toate alimentările în ordine cronologică · click pe persoană / autoturism / chip pentru detalii.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/consumuri-masini")} data-testid="goto-consumuri-btn">
            <Gauge size={14} className="mr-1.5"/> Consumuri mașini
          </Button>
          <Button variant="outline" onClick={() => navigate("/autoturisme")}>
            <CarIcon size={14} className="mr-1.5"/> Vezi flota
          </Button>
        </div>
      </div>

      {/* Quick add form */}
      <form onSubmit={submit} className="card-flat p-5" data-testid="quick-fuel-form">
        <div className="flex items-center gap-2 mb-3">
          <Gauge size={18} className="text-[#D97706]" />
          <h2 className="font-display text-lg font-bold">Înregistrează alimentare</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Label className="small-label">Mașina</Label>
            <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
              <SelectTrigger data-testid="fuel-car-select" className="mt-1">
                <SelectValue placeholder="Alege mașina"/>
              </SelectTrigger>
              <SelectContent>
                {cars.map((c) => <SelectItem key={c.id} value={c.id}>{carLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="small-label">Dată</Label>
            <Input type="date" data-testid="fuel-date" className="mt-1" value={form.data_alimentare} onChange={(e) => setForm({ ...form, data_alimentare: e.target.value })}/>
          </div>
          <div>
            <Label className="small-label">Litri</Label>
            <Input type="number" step="0.01" min="0" data-testid="fuel-litri" className="mt-1" value={form.litri} onChange={(e) => setForm({ ...form, litri: e.target.value })} placeholder="ex: 45.20"/>
          </div>
          <div>
            <Label className="small-label">Km curent</Label>
            <Input type="number" min="0" data-testid="fuel-km" className="mt-1" value={form.numar_km} onChange={(e) => setForm({ ...form, numar_km: e.target.value })} placeholder="ex: 125000"/>
          </div>
          <div>
            <Label className="small-label">Nr. chip / card (opțional)</Label>
            <Input data-testid="fuel-chip" className="mt-1" value={form.numar_chip} onChange={(e) => setForm({ ...form, numar_chip: e.target.value })}/>
          </div>
          <div className="lg:col-span-2">
            <Label className="small-label">Persoana care a alimentat</Label>
            <Input
              list="combustibil-person-options"
              data-testid="fuel-persoana"
              className="mt-1"
              value={form.persoana}
              onChange={(e) => setForm({ ...form, persoana: e.target.value })}
              placeholder="Alege din listă sau scrie numele..."
              autoComplete="off"
            />
            <datalist id="combustibil-person-options">
              {persons
                .map((p) => p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim())
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b, "ro"))
                .map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button type="submit" disabled={loading} data-testid="submit-quick-fuel" className="bg-[#D97706] hover:bg-[#B45309]">
            <Droplet size={14} className="mr-1.5"/> {loading ? "Se salvează…" : "Salvează alimentare"}
          </Button>
        </div>
      </form>

      {/* View mode toggle + search */}
      <div className="card-flat p-3 flex items-center gap-2 flex-wrap" data-testid="combustibil-toolbar">
        {[
          { v: "list", l: "Listă", icon: List },
          { v: "by_chip", l: "După chip", icon: CreditCard },
          { v: "by_person", l: "După persoană", icon: User },
          { v: "by_car", l: "După autoturism", icon: CarIcon },
        ].map(({ v, l, icon: Icon }) => (
          <button
            key={v}
            type="button"
            onClick={() => setViewMode(v)}
            className={`text-xs font-bold px-3 py-1.5 rounded-md border-2 transition-colors flex items-center gap-1.5 ${viewMode === v ? "bg-[#D97706] text-white border-[#D97706]" : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
            data-testid={`view-mode-${v}`}
          >
            <Icon size={12}/> {l}
          </button>
        ))}
        <span className="flex-1"/>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută persoană, chip, dată, mașină..."
            className="pl-8 w-72"
            data-testid="combustibil-search"
          />
        </div>
      </div>

      {/* === LIST VIEW === */}
      {viewMode === "list" && (
        <div className="card-flat">
          <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
            <h3 className="font-display font-bold">Ultimele alimentări (cronologic)</h3>
            <div className="text-xs text-zinc-500">
              {filtered.length} intrări · <strong className="text-[#D97706]">{totalLiters.toFixed(2)} L</strong> total
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="p-3">Data</th>
                  <th className="p-3">Autoturism</th>
                  <th className="p-3">Chip</th>
                  <th className="p-3 text-right">Litri</th>
                  <th className="p-3 text-right">Km</th>
                  <th className="p-3">Persoană</th>
                  {isAdmin && <th className="p-3 text-right w-[90px]">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--border)] row-hover">
                    <td className="p-3 tabular-nums whitespace-nowrap">{fmtRoDate(r.data_alimentare)}</td>
                    <td className="p-3 font-medium">
                      <button
                        type="button"
                        onClick={() => setDrill({ type: "car", key: r.car_label, label: r.car_label })}
                        className="text-zinc-800 hover:text-[#D97706] hover:underline text-left"
                        data-testid={`drill-car-${r.id}`}
                        title="Vezi ce chip-uri a folosit acest autoturism"
                      >
                        {r.car_label || "—"}
                      </button>
                    </td>
                    <td className="p-3">
                      {r.numar_chip ? (
                        <button
                          type="button"
                          onClick={() => setDrill({ type: "chip", key: r.numar_chip, label: r.numar_chip })}
                          className="inline-flex items-center gap-1 text-xs font-mono font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                          data-testid={`drill-chip-${r.id}`}
                          title="Vezi toate alimentările cu acest chip"
                        >
                          💳 {r.numar_chip}
                        </button>
                      ) : <span className="text-zinc-300 text-xs">—</span>}
                    </td>
                    <td className="p-3 text-right tabular-nums text-[#D97706] font-semibold">{Number(r.litri || 0).toFixed(2)}</td>
                    <td className="p-3 text-right tabular-nums">{Number(r.numar_km || 0).toLocaleString("ro-RO")}</td>
                    <td className="p-3">
                      {r.persoana ? (
                        <button
                          type="button"
                          onClick={() => setDrill({ type: "person", key: r.persoana, label: r.persoana })}
                          className="text-zinc-700 hover:text-[#D97706] hover:underline"
                          data-testid={`drill-person-${r.id}`}
                          title="Vezi ce autoturisme a alimentat această persoană"
                        >
                          {r.persoana}
                        </button>
                      ) : <span className="text-zinc-300">—</span>}
                      <AuditLine entry={r} testid={`audit-${r.id}`} />
                    </td>
                    {isAdmin && (
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing(r)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-amber-50 text-zinc-500 hover:text-amber-700 transition-colors"
                          data-testid={`edit-fuel-${r.id}`}
                          title="Editează alimentare"
                        >
                          <Pencil size={14}/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={isAdmin ? 7 : 6} className="p-6 text-center text-zinc-400 italic">Nicio alimentare {q ? "care să corespundă căutării" : "înregistrată"}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === GROUPED VIEWS === */}
      {viewMode !== "list" && (
        <GroupedView
          groups={viewMode === "by_chip" ? groupChip : viewMode === "by_person" ? groupPerson : groupCar}
          type={viewMode === "by_chip" ? "chip" : viewMode === "by_person" ? "person" : "car"}
          onDrill={(g) => setDrill({ type: viewMode === "by_chip" ? "chip" : viewMode === "by_person" ? "person" : "car", key: g.key, label: g.key })}
        />
      )}

      {/* Tabel consumuri per mașină */}
      <FuelConsumptionTable />

      {/* Drill-down dialog */}
      {drill && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) setDrill(null); }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="drill-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 pr-8">
                {drill.type === "chip" && <CreditCard size={18} className="text-blue-600"/>}
                {drill.type === "person" && <User size={18} className="text-emerald-600"/>}
                {drill.type === "car" && <CarIcon size={18} className="text-amber-700"/>}
                <span>
                  {drill.type === "chip" && "Chip "}
                  {drill.type === "person" && "Persoană "}
                  {drill.type === "car" && "Autoturism "}
                  <strong className="font-mono">{drill.label}</strong>
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Context inversă */}
              {drillContext.rows.length > 0 && (
                <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    {drillContext.label} ({drillContext.rows.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {drillContext.rows.map((c) => (
                      <span key={c.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-zinc-200 text-xs">
                        <strong>{c.key}</strong>
                        <span className="text-zinc-400">·</span>
                        <span className="text-zinc-600">{c.count}×</span>
                        <span className="text-zinc-400">·</span>
                        <span className="text-[#D97706] font-semibold">{c.liters.toFixed(2)} L</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Listă alimentări */}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  Alimentări ({drillItems.length}) · Total: <strong className="text-[#D97706]">{drillItems.reduce((a, r) => a + (Number(r.litri) || 0), 0).toFixed(2)} L</strong>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr className="text-left">
                        <th className="p-2">Data</th>
                        {drill.type !== "car" && <th className="p-2">Autoturism</th>}
                        {drill.type !== "chip" && <th className="p-2">Chip</th>}
                        {drill.type !== "person" && <th className="p-2">Persoană</th>}
                        <th className="p-2 text-right">Litri</th>
                        <th className="p-2 text-right">Km</th>
                        {isAdmin && <th className="p-2 text-right w-[60px]"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {drillItems.map((r) => (
                        <tr key={r.id} className="border-t border-[var(--border)]">
                          <td className="p-2 tabular-nums whitespace-nowrap">{fmtRoDate(r.data_alimentare)}</td>
                          {drill.type !== "car" && <td className="p-2">{r.car_label || "—"}</td>}
                          {drill.type !== "chip" && <td className="p-2 font-mono">{r.numar_chip || "—"}</td>}
                          {drill.type !== "person" && <td className="p-2">{r.persoana || "—"}{<AuditLine entry={r} testid={`drill-audit-${r.id}`} compact />}</td>}
                          <td className="p-2 text-right tabular-nums text-[#D97706] font-semibold">{Number(r.litri || 0).toFixed(2)}</td>
                          <td className="p-2 text-right tabular-nums">{Number(r.numar_km || 0).toLocaleString("ro-RO")}</td>
                          {isAdmin && (
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                onClick={() => { setDrill(null); setEditing(r); }}
                                className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-amber-50 text-zinc-500 hover:text-amber-700 transition-colors"
                                data-testid={`drill-edit-fuel-${r.id}`}
                                title="Editează"
                              >
                                <Pencil size={12}/>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => setDrill(null)} variant="outline" data-testid="drill-close"><X size={14} className="mr-1"/> Închide</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit fuel dialog (admin only) */}
      {editing && (
        <EditFuelDialog
          entry={editing}
          cars={cars}
          persons={persons}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
      <div className="mt-8 card-flat p-6">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
          📱 Cronologie Alimentări
          <span className="text-xs text-zinc-500 font-normal">— cine a alimentat / editat / când</span>
        </h2>
        <ActivityFeed category="fuel" pageSize={8} compact/>
      </div>
    </div>
  );
}

function EditFuelDialog({ entry, cars, persons, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    car_id: entry.car_id || "",
    data_alimentare: entry.data_alimentare || todayISO(),
    numar_chip: entry.numar_chip || "",
    litri: entry.litri != null ? String(entry.litri) : "",
    numar_km: entry.numar_km != null ? String(entry.numar_km) : "",
    persoana: entry.persoana || "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    if (!form.car_id) { toast.error("Lipsește autoturismul"); return; }
    if (!form.litri || Number(form.litri) <= 0) { toast.error("Litrii trebuie > 0"); return; }
    if (!form.numar_km || Number(form.numar_km) <= 0) { toast.error("Km trebuie > 0"); return; }
    setSaving(true);
    try {
      await api.put(`/cars/${form.car_id}/fuel/${entry.id}`, {
        data_alimentare: form.data_alimentare,
        numar_chip: form.numar_chip || null,
        litri: Number(form.litri),
        numar_km: Number(form.numar_km),
        persoana: form.persoana || null,
      });
      toast.success("Alimentare actualizată");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Ești sigur că vrei să ștergi această alimentare? Acțiunea NU poate fi anulată.")) return;
    setDeleting(true);
    try {
      await api.delete(`/cars/${entry.car_id}/fuel/${entry.id}`);
      toast.success("Alimentare ștearsă");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-fuel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={18} className="text-amber-700"/> Editează alimentare
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Autoturism *</Label>
            <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
              <SelectTrigger className="mt-1" data-testid="edit-fuel-car"><SelectValue placeholder="Selectează…"/></SelectTrigger>
              <SelectContent>
                {cars.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {[c.intern_number ? `Auto ${c.intern_number}` : null, c.numar_inmatriculare, [c.marca, c.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data *</Label>
              <Input type="date" value={form.data_alimentare} onChange={(e) => setForm({ ...form, data_alimentare: e.target.value })} data-testid="edit-fuel-date"/>
            </div>
            <div>
              <Label>Chip</Label>
              <Input value={form.numar_chip} onChange={(e) => setForm({ ...form, numar_chip: e.target.value })} placeholder="ex: 12345" data-testid="edit-fuel-chip"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Litri *</Label>
              <Input type="number" step="0.01" min="0" value={form.litri} onChange={(e) => setForm({ ...form, litri: e.target.value })} data-testid="edit-fuel-litri"/>
            </div>
            <div>
              <Label>Km *</Label>
              <Input type="number" step="1" min="0" value={form.numar_km} onChange={(e) => setForm({ ...form, numar_km: e.target.value })} data-testid="edit-fuel-km"/>
            </div>
          </div>
          <div>
            <Label>Persoana care a alimentat</Label>
            <Input
              list="edit-fuel-person-options"
              value={form.persoana}
              onChange={(e) => setForm({ ...form, persoana: e.target.value })}
              placeholder="Nume persoană"
              data-testid="edit-fuel-persoana"
            />
            <datalist id="edit-fuel-person-options">
              {persons.map((p) => (
                <option key={p.id} value={`${p.nume || ""} ${p.prenume || ""}`.trim()}/>
              ))}
            </datalist>
          </div>
        </div>
        <div className="flex justify-between gap-2 pt-3 border-t border-zinc-200 mt-3">
          <Button variant="outline" onClick={del} disabled={saving || deleting} className="text-red-600 hover:bg-red-50 border-red-200" data-testid="edit-fuel-delete">
            <Trash2 size={14} className="mr-1"/> {deleting ? "Se șterge…" : "Șterge"}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting} data-testid="edit-fuel-cancel">Anulează</Button>
            <Button onClick={save} disabled={saving || deleting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-fuel-save">
              {saving ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupedView({ groups, type, onDrill }) {
  const icon = type === "chip" ? CreditCard : type === "person" ? User : CarIcon;
  const Icon = icon;
  const colorCls = type === "chip" ? "text-blue-700 bg-blue-50 border-blue-200"
    : type === "person" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-amber-700 bg-amber-50 border-amber-200";
  return (
    <div className="card-flat p-3" data-testid={`grouped-${type}`}>
      {groups.length === 0 ? (
        <div className="p-6 text-center text-zinc-400 italic">Nicio grupare disponibilă.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => onDrill(g)}
              className={`card-flat p-4 text-left hover:border-[#D97706] transition-colors group ${colorCls}`}
              data-testid={`group-card-${type}-${g.key}`}
            >
              <div className="flex items-start justify-between">
                <Icon size={18}/>
                <ArrowRight size={14} className="opacity-50 group-hover:opacity-100 transition-opacity"/>
              </div>
              <div className="mt-3 font-display font-bold text-sm break-all">{g.key}</div>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-700">
                <span><strong>{g.total_fills}</strong> alimentări</span>
                <span>·</span>
                <span><strong className="text-[#D97706]">{g.total_liters.toFixed(2)} L</strong></span>
              </div>
              <div className="mt-1 text-[10px] text-zinc-500">ultima: {fmtRoDate(g.last_date)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
